// Visual card mockups for the Plans display.
// Aspect ratio is the CR80 standard (1.586:1).

const VARIANTS = {
  black: {
    label: 'Basic',
    last4: '5467',
    network: 'mastercard',
    text: '#f4f4f4',
    muted: 'rgba(255,255,255,0.68)',
    chipFill: '#202020',
    chipStroke: 'rgba(255,255,255,0.10)',
    lineA: 'rgba(255,255,255,0.28)',
    lineB: 'rgba(0,161,214,0.46)',
    lineC: 'rgba(229,42,132,0.38)',
    background:
      'radial-gradient(circle at 18% 12%, rgba(255,255,255,0.045), transparent 35%), linear-gradient(145deg, #070707 0%, #010101 54%, #090909 100%)',
    shadow:
      '0 24px 44px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 0 0 1px rgba(255,255,255,0.05)',
  },
  glass: {
    label: 'Pulsar',
    tier: 'Pro',
    last4: '7891',
    network: 'visa',
    text: '#1f1f1f',
    muted: 'rgba(31,31,31,0.54)',
    chipFill: 'rgba(255,255,255,0.24)',
    chipStroke: 'rgba(31,31,31,0.12)',
    lineA: 'rgba(45,102,148,0.22)',
    lineB: 'rgba(151,194,236,0.42)',
    lineC: 'rgba(228,252,208,0.46)',
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.76), rgba(239,246,252,0.38) 48%, rgba(255,255,255,0.28) 100%)',
    shadow:
      '0 24px 52px rgba(92,151,194,0.18), inset 0 1px 0 rgba(255,255,255,0.92), inset 0 0 0 1px rgba(255,255,255,0.72)',
  },
  platinum: {
    label: 'Pulsar',
    tier: 'Premium',
    last4: '5050',
    network: 'visa',
    text: '#171717',
    muted: 'rgba(31,31,31,0.52)',
    chipFill: 'rgba(255,255,255,0.20)',
    chipStroke: 'rgba(31,31,31,0.13)',
    lineA: 'rgba(31,31,31,0.18)',
    lineB: 'rgba(255,255,255,0.52)',
    lineC: 'rgba(92,151,194,0.24)',
    background:
      'linear-gradient(120deg, #f4f4f0 0%, #c4ccd2 24%, #ffffff 44%, #9da6ad 66%, #ecefed 100%)',
    shadow:
      '0 26px 50px rgba(20,20,20,0.20), inset 0 1px 0 rgba(255,255,255,0.92), inset 0 0 0 1px rgba(255,255,255,0.46)',
  },
};

const REAL_CARD_TREATMENTS = {
  black: {
    imageOpacity: 1,
  },
  glass: {
    imageOpacity: 0.52,
    imageFilter: 'grayscale(1) brightness(1.9) contrast(0.32)',
    cardOverlay:
      'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(239,246,252,0.42) 45%, rgba(255,255,255,0.18) 100%)',
    cardOverlayBlend: 'normal',
    cardOverlayOpacity: 0.78,
    cardBoxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.82), inset 0 0 0 1px rgba(255,255,255,0.58), 0 22px 46px rgba(92,151,194,0.18)',
    cardBackdropFilter: 'blur(16px) saturate(1.35)',
    shine:
      'linear-gradient(112deg, transparent 0%, rgba(255,255,255,0.68) 34%, rgba(151,194,236,0.22) 56%, transparent 78%)',
    shineOpacity: 0.88,
  },
  platinum: {
    imageOpacity: 0.88,
    imageFilter: 'grayscale(1) contrast(0.86) brightness(1.12)',
    cardOverlay:
      'linear-gradient(120deg, rgba(247,248,246,0.9) 0%, rgba(164,173,181,0.48) 24%, rgba(255,255,255,0.84) 45%, rgba(135,145,154,0.5) 68%, rgba(238,241,239,0.76) 100%)',
    cardOverlayBlend: 'screen',
    cardOverlayOpacity: 0.86,
    shine:
      'repeating-linear-gradient(115deg, rgba(255,255,255,0.34) 0, rgba(255,255,255,0.34) 1px, transparent 1px, transparent 10px)',
    shineOpacity: 0.5,
  },
};

export default function CardMockup({ variant = 'black', className = '' }) {
  if (variant === 'glass') {
    return <TransparentProCard className={className} />;
  }

  const realCardTreatment = REAL_CARD_TREATMENTS[variant];
  if (realCardTreatment) {
    return <RealCardImage treatment={realCardTreatment} className={className} />;
  }

  const v = VARIANTS[variant] ?? VARIANTS.black;

  return (
    <div className={`relative mx-auto w-full max-w-[370px] ${className}`} aria-hidden="true">
      <div
        className="relative overflow-hidden"
        style={{
          aspectRatio: '1.586 / 1',
          borderRadius: 18,
          background: v.background,
          color: v.text,
          boxShadow: v.shadow,
          backdropFilter: variant === 'glass' ? 'blur(18px)' : undefined,
          WebkitBackdropFilter: variant === 'glass' ? 'blur(18px)' : undefined,
        }}
      >
        <MaterialOverlay variant={variant} />
        <CardLines variant={variant} {...v} />

        <div className="absolute z-10" style={{ left: '5.6%', top: '7.2%' }}>
          {variant === 'black' ? (
            <span className="text-[14px] font-medium leading-none" style={{ color: v.text }}>
              {v.label}
            </span>
          ) : (
            <CardBrand color={v.text} />
          )}
        </div>

        {variant !== 'black' && (
          <span
            className="absolute z-10 text-[10px] font-medium uppercase tracking-[0.24em]"
            style={{ color: v.muted, right: '8.4%', top: '8%' }}
          >
            {v.tier}
          </span>
        )}

        <PulsarMark
          className="absolute z-10"
          style={{
            color: variant === 'black' ? 'rgba(255,255,255,0.72)' : 'rgba(31,31,31,0.18)',
            height: '18%',
            right: '6.6%',
            top: '7.2%',
            width: '12%',
          }}
        />

        <Chip
          className="absolute z-10"
          fill={v.chipFill}
          stroke={v.chipStroke}
          style={{ height: '16%', left: '8.2%', top: '33%', width: '13.4%' }}
        />

        <div
          className="absolute z-10 flex items-baseline gap-2"
          style={{ bottom: '10.2%', left: '8.6%' }}
        >
          <span
            className="text-[13px] leading-none"
            style={{ color: v.muted, letterSpacing: '0.14em' }}
          >
            ○○○○
          </span>
          <span
            className="text-[23px] font-medium leading-none num-tabular"
            style={{ color: v.text, letterSpacing: '0.02em' }}
          >
            {v.last4}
          </span>
        </div>

        <div className="absolute z-10" style={{ bottom: '7.2%', right: '6.8%' }}>
          <CardNetwork type={v.network} color={v.text} />
        </div>
      </div>
    </div>
  );
}

function TransparentProCard({ className = '' }) {
  return (
    <div className={`relative mx-auto w-full max-w-[330px] ${className}`} aria-hidden="true">
      <div
        className="relative overflow-hidden"
        style={{
          aspectRatio: '1.586 / 1',
          borderRadius: 18,
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.30), rgba(183,232,229,0.16) 52%, rgba(255,255,255,0.26))',
          border: '1px solid rgba(255,255,255,0.76)',
          boxShadow:
            '0 24px 50px rgba(67,127,171,0.18), inset 0 1px 0 rgba(255,255,255,0.82), inset 0 -1px 0 rgba(255,255,255,0.35)',
          backdropFilter: 'blur(18px) saturate(1.35)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.35)',
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(110deg, rgba(255,255,255,0.38), transparent 35%, rgba(151,194,236,0.18) 58%, rgba(255,255,255,0.22))',
          }}
        />

        <div
          className="absolute"
          style={{
            background: 'linear-gradient(180deg, rgba(22,22,22,0.92), rgba(18,18,18,0.8))',
            border: '1px solid rgba(201,167,128,0.34)',
            borderRadius: 5,
            height: '25%',
            left: '5.8%',
            right: '5.8%',
            top: '8.8%',
          }}
        />

        <div
          className="absolute"
          style={{
            border: '1px solid rgba(201,167,128,0.42)',
            borderRadius: 8,
            height: '42%',
            left: '5.8%',
            right: '5.8%',
            top: '33.8%',
          }}
        />

        <div
          className="absolute flex items-center gap-2 text-white"
          style={{ left: '10%', top: '15%' }}
        >
          <PulsarMark className="h-7 w-6" />
          <span className="text-[18px] font-semibold leading-none">Pulsar</span>
        </div>
        <span
          className="absolute text-[10px] font-medium uppercase tracking-[0.22em] text-white/70"
          style={{ right: '8.8%', top: '16%' }}
        >
          Pro
        </span>

        <Chip
          className="absolute z-10"
          fill="rgba(255,255,255,0.34)"
          stroke="rgba(31,31,31,0.22)"
          style={{ height: '17%', left: '12%', top: '40%', width: '14.8%' }}
        />

        <ContactlessIcon />

        <div className="absolute z-10" style={{ bottom: '12.5%', right: '9%' }}>
          <CardNetwork type="mastercard" size="xs" />
        </div>
      </div>
    </div>
  );
}

function RealCardImage({ treatment, className = '' }) {
  return (
    <div className={`relative mx-auto w-full max-w-[370px] ${className}`} aria-hidden="true">
      <img
        src="/cards/pulsar-basic-card.png"
        alt=""
        className="block h-auto w-full select-none"
        draggable="false"
        style={{
          filter: treatment.imageFilter,
          opacity: treatment.imageOpacity,
        }}
      />
      {treatment.cardOverlay && (
        <div
          className="pointer-events-none absolute"
          style={{
            background: treatment.cardOverlay,
            borderRadius: '4.4%',
            boxShadow: treatment.cardBoxShadow,
            backdropFilter: treatment.cardBackdropFilter,
            height: '76%',
            left: '5.8%',
            mixBlendMode: treatment.cardOverlayBlend,
            opacity: treatment.cardOverlayOpacity,
            top: '9.6%',
            width: '88.4%',
            WebkitBackdropFilter: treatment.cardBackdropFilter,
          }}
        />
      )}
      {treatment.shine && (
        <div
          className="pointer-events-none absolute"
          style={{
            background: treatment.shine,
            borderRadius: '4.4%',
            height: '76%',
            left: '5.8%',
            mixBlendMode: 'screen',
            opacity: treatment.shineOpacity,
            top: '9.6%',
            width: '88.4%',
          }}
        />
      )}
    </div>
  );
}

function MaterialOverlay({ variant }) {
  if (variant === 'glass') {
    return (
      <>
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(112deg, rgba(255,255,255,0.68) 0%, rgba(255,255,255,0.06) 38%, rgba(151,194,236,0.16) 64%, rgba(255,255,255,0.36) 100%)',
          }}
        />
        <div
          className="absolute inset-x-0 top-0 h-1/2"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.38), transparent)',
          }}
        />
      </>
    );
  }

  if (variant === 'platinum') {
    return (
      <>
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.55) 33%, rgba(255,255,255,0.10) 46%, transparent 70%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-25"
          style={{
            background:
              'repeating-linear-gradient(115deg, rgba(255,255,255,0.24) 0, rgba(255,255,255,0.24) 1px, transparent 1px, transparent 11px)',
          }}
        />
      </>
    );
  }

  return (
    <div
      className="absolute inset-0"
      style={{
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.028), transparent 35%), radial-gradient(circle at 86% 90%, rgba(255,154,10,0.05), transparent 30%)',
      }}
    />
  );
}

function CardLines({ variant, lineA, lineB, lineC }) {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 512 323"
      fill="none"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={`${variant}-card-line`} x1="0" y1="0" x2="512" y2="323">
          <stop offset="0" stopColor={lineA} />
          <stop offset="0.34" stopColor={lineB} />
          <stop offset="0.62" stopColor={lineA} />
          <stop offset="1" stopColor={lineC} />
        </linearGradient>
      </defs>
      <path
        d="M0 0C38 90 118 98 210 98H512"
        stroke={`url(#${variant}-card-line)`}
        strokeWidth="1.35"
      />
      <path
        d="M0 178H154C158 231 113 279 55 287"
        stroke={`url(#${variant}-card-line)`}
        strokeWidth="1.25"
      />
      <path
        d="M191 323V139C259 139 306 190 306 259V323"
        stroke={`url(#${variant}-card-line)`}
        strokeWidth="1.35"
      />
    </svg>
  );
}

function Chip({ className, fill, stroke, style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 56 42" fill="none">
      <rect
        x="0.75"
        y="0.75"
        width="54.5"
        height="40.5"
        rx="8"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
      />
      <path
        d="M18 1V41M38 1V41M1 14H18M38 14H55M1 28H18M38 28H55"
        stroke={stroke}
        strokeWidth="1.35"
      />
    </svg>
  );
}

function ContactlessIcon() {
  return (
    <svg
      className="absolute z-10"
      style={{ height: '16%', left: '36%', top: '41%', width: '13%' }}
      viewBox="0 0 48 48"
      fill="none"
    >
      <path
        d="M14 16C18 20.5 18 27.5 14 32"
        stroke="#1f1f1f"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path d="M23 12C29 19 29 29 23 36" stroke="#1f1f1f" strokeWidth="3" strokeLinecap="round" />
      <path d="M32 8C41 18 41 30 32 40" stroke="#1f1f1f" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function CardBrand({ color }) {
  return (
    <div
      className="flex items-center gap-2 text-[20px] font-semibold leading-none"
      style={{ color }}
    >
      <PulsarMark className="h-8 w-7" />
      <span>Pulsar</span>
    </div>
  );
}

function CardNetwork({ type, color, size = 'md' }) {
  if (type === 'mastercard') {
    const dimsBySize = {
      xs: { h: 30, w: 46, circle: 30 },
      sm: { h: 38, w: 58, circle: 38 },
      md: { h: 54, w: 84, circle: 54 },
    };
    const dims = dimsBySize[size] ?? dimsBySize.md;
    return (
      <div style={{ height: dims.h, position: 'relative', width: dims.w }}>
        <span
          style={{
            background: '#ff140d',
            borderRadius: '999px',
            bottom: 0,
            height: dims.circle,
            left: 0,
            position: 'absolute',
            width: dims.circle,
          }}
        />
        <span
          style={{
            background: '#ff9a09',
            borderRadius: '999px',
            bottom: 0,
            height: dims.circle,
            mixBlendMode: 'screen',
            position: 'absolute',
            right: 0,
            width: dims.circle,
          }}
        />
      </div>
    );
  }

  return (
    <span
      className="text-[20px] font-bold italic leading-none tracking-[-0.06em]"
      style={{ color }}
    >
      VISA
    </span>
  );
}

function PulsarMark({ className = 'h-5 w-5', style }) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 50 60"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M0.003 0H30.717C41.365 0 49.997 8.632 49.997 19.28C49.997 29.928 41.365 38.56 30.717 38.56C23.818 38.528 17.114 35.012 17.113 27.136L20.929 27.136H28.652C32.99 27.136 36.507 23.619 36.507 19.28C36.507 14.942 32.99 11.425 28.652 11.425H0.003V0ZM13.254 14.641V27.136V38.56V38.695V59.906H0.003V38.56V27.892C0.003 27.638 0.010 27.386 0.024 27.136C0.416 20.169 6.189 14.641 13.254 14.641Z" />
    </svg>
  );
}
